import type { Job } from '../types/content';

export const SAMPLE_JOBS_NOTICE =
  'All listings shown are sample vacancies created for demonstration. They are not confirmed, currently available positions.';

export const jobs: Job[] = [
  {
    id: 'chief-financial-officer-london',
    ref: 'CTS-SAMPLE-1042',
    title: 'Chief Financial Officer',
    location: 'London, UK',
    arrangement: 'Hybrid',
    type: 'Permanent',
    seniority: 'C-Suite',
    industry: 'financial-services',
    salary: 'Competitive — disclosed to shortlisted candidates',
    posted: '2026-10-20',
    featured: true,
    summary: 'Lead finance, capital and investor relations for a growing regulated lender.',
    overview:
      'A regulated specialist lender is seeking a Chief Financial Officer to lead its finance function through its next phase of growth. Reporting to the Chief Executive and working closely with the board, the CFO will own financial strategy, capital planning and investor relationships.',
    responsibilities: [
      'Set and deliver financial strategy aligned to the group’s growth plan',
      'Lead capital, liquidity and funding planning with treasury and risk teams',
      'Act as the primary finance voice to the board, regulators and investors',
      'Build and develop a high-performing finance leadership team',
      'Strengthen financial controls, reporting and forecasting discipline',
    ],
    qualifications: [
      'Qualified accountant (ACA, ACCA, CIMA or equivalent)',
      'Senior finance leadership experience in a regulated financial-services business',
      'Experience presenting to boards and engaging with regulators',
      'Track record of leading teams through growth or change',
    ],
    competencies: ['Strategic financial judgement', 'Board-level communication', 'Regulatory awareness', 'Team leadership'],
  },
  {
    id: 'chief-technology-officer-remote',
    ref: 'CTS-SAMPLE-1043',
    title: 'Chief Technology Officer',
    location: 'Remote — EMEA',
    arrangement: 'Remote',
    type: 'Permanent',
    seniority: 'C-Suite',
    industry: 'technology',
    posted: '2026-10-18',
    featured: true,
    summary: 'Shape the technology vision of a scaling B2B software company.',
    overview:
      'A B2B software company is appointing a Chief Technology Officer to lead engineering, architecture and platform strategy. The CTO will partner with the CEO and product leadership to scale the platform, the team and the engineering culture.',
    responsibilities: [
      'Own technology strategy, architecture and platform roadmap',
      'Lead and scale a distributed engineering organisation',
      'Partner with product leadership on delivery and prioritisation',
      'Establish strong security, reliability and engineering practices',
      'Represent technology to the board and key customers',
    ],
    qualifications: [
      'Senior engineering leadership experience in a SaaS environment',
      'Experience scaling distributed engineering teams',
      'Strong grounding in modern cloud architecture',
      'Commercial awareness and customer-facing confidence',
    ],
    competencies: ['Technical vision', 'Organisational design', 'Stakeholder influence', 'Delivery discipline'],
  },
  {
    id: 'non-executive-director-audit-chair',
    ref: 'CTS-SAMPLE-1044',
    title: 'Non-Executive Director & Audit Committee Chair',
    location: 'London, UK',
    arrangement: 'Hybrid',
    type: 'Fixed-term',
    seniority: 'Board',
    industry: 'healthcare',
    salary: 'Fee disclosed on application',
    posted: '2026-10-16',
    featured: true,
    summary: 'Chair the audit committee of a life-sciences group’s board.',
    overview:
      'A life-sciences group is seeking an independent Non-Executive Director to chair its Audit Committee for an initial three-year term. The appointee will bring recent and relevant financial experience and strengthen the board’s oversight of reporting, risk and controls.',
    responsibilities: [
      'Chair the Audit Committee and oversee the external audit relationship',
      'Provide independent challenge on financial reporting and controls',
      'Contribute to board strategy, risk appetite and governance',
      'Support the development of the finance and internal audit functions',
    ],
    qualifications: [
      'Recent and relevant financial experience at senior level',
      'Prior board or committee experience preferred',
      'Understanding of regulated or science-led businesses',
      'Independence from the organisation and its advisers',
    ],
    competencies: ['Independent judgement', 'Governance expertise', 'Constructive challenge', 'Integrity'],
  },
  {
    id: 'interim-group-hr-director',
    ref: 'CTS-SAMPLE-1045',
    title: 'Interim Group HR Director',
    location: 'Manchester, UK',
    arrangement: 'On-site',
    type: 'Interim',
    seniority: 'Executive',
    industry: 'manufacturing',
    salary: 'Day rate disclosed on application',
    posted: '2026-10-14',
    summary: 'Lead people strategy through a multi-site operational restructure.',
    overview:
      'A multi-site manufacturer requires an experienced Interim Group HR Director for approximately nine months to lead the people workstream of an operational restructure and stabilise the HR function.',
    responsibilities: [
      'Lead the people workstream of an operational restructure',
      'Advise the executive team on organisation design and change',
      'Manage employee relations and consultation processes',
      'Stabilise and develop the HR leadership team',
    ],
    qualifications: [
      'Senior HR leadership in multi-site industrial environments',
      'Experience leading consultation and restructuring',
      'Proven interim or transformation assignments',
      'Strong employee-relations expertise',
    ],
    competencies: ['Change leadership', 'Employee relations', 'Pragmatism', 'Executive influence'],
  },
  {
    id: 'head-of-regulatory-affairs-energy',
    ref: 'CTS-SAMPLE-1046',
    title: 'Head of Regulatory Affairs',
    location: 'Lagos, Nigeria',
    arrangement: 'Hybrid',
    type: 'Permanent',
    seniority: 'Senior Management',
    industry: 'energy',
    posted: '2026-10-12',
    summary: 'Lead regulatory strategy for an energy business in a changing market.',
    overview:
      'An energy company is recruiting a Head of Regulatory Affairs to lead its engagement with regulators and policymakers and to shape its regulatory strategy as the market evolves.',
    responsibilities: [
      'Develop and deliver the company’s regulatory strategy',
      'Lead engagement with regulators and industry bodies',
      'Advise leadership on regulatory risk and opportunity',
      'Coordinate submissions, consultations and compliance reporting',
    ],
    qualifications: [
      'Significant regulatory experience in energy or utilities',
      'Experience engaging with regulators and government bodies',
      'Strong analytical and written communication skills',
      'Degree in law, economics, engineering or a related field',
    ],
    competencies: ['Policy analysis', 'Stakeholder engagement', 'Discretion', 'Strategic thinking'],
  },
  {
    id: 'director-digital-transformation-dubai',
    ref: 'CTS-SAMPLE-1047',
    title: 'Director of Digital Transformation',
    location: 'Dubai, UAE',
    arrangement: 'Hybrid',
    type: 'Permanent',
    seniority: 'Executive',
    industry: 'telecoms-media',
    posted: '2026-10-10',
    featured: true,
    summary: 'Lead enterprise-wide transformation for a regional telecoms operator.',
    overview:
      'A regional telecommunications operator is seeking a Director of Digital Transformation to lead an enterprise-wide programme spanning customer experience, operations and data.',
    responsibilities: [
      'Define and lead the transformation roadmap',
      'Align executive stakeholders around priorities and outcomes',
      'Oversee delivery partners and internal programme teams',
      'Embed data-driven decision-making across the business',
    ],
    qualifications: [
      'Experience leading large-scale digital transformation',
      'Background in telecoms, media or technology',
      'Strong programme governance experience',
      'Experience working across multicultural teams',
    ],
    competencies: ['Transformation leadership', 'Executive alignment', 'Commercial acumen', 'Resilience'],
  },
  {
    id: 'general-counsel-company-secretary',
    ref: 'CTS-SAMPLE-1048',
    title: 'General Counsel & Company Secretary',
    location: 'New York, USA',
    arrangement: 'Hybrid',
    type: 'Permanent',
    seniority: 'Executive',
    industry: 'legal-risk',
    posted: '2026-10-08',
    summary: 'Lead legal, governance and company secretarial matters for a listed group.',
    overview:
      'A listed group is appointing a General Counsel & Company Secretary to lead its legal function and advise the board on governance, risk and strategic transactions.',
    responsibilities: [
      'Lead the legal and company secretarial functions',
      'Advise the board and executive on legal risk and governance',
      'Oversee transactions, disputes and external counsel',
      'Maintain strong compliance and ethics frameworks',
    ],
    qualifications: [
      'Qualified lawyer with senior in-house experience',
      'Listed-company governance experience',
      'Transactional and commercial expertise',
      'Experience managing external counsel budgets',
    ],
    competencies: ['Commercial judgement', 'Governance', 'Integrity', 'Calm under pressure'],
  },
  {
    id: 'senior-data-privacy-manager',
    ref: 'CTS-SAMPLE-1049',
    title: 'Senior Data Privacy Manager',
    location: 'Remote — UK',
    arrangement: 'Remote',
    type: 'Contract',
    seniority: 'Specialist',
    industry: 'professional-services',
    salary: 'Day rate disclosed on application',
    posted: '2026-10-06',
    summary: 'Strengthen privacy governance for an advisory firm on a 12-month contract.',
    overview:
      'A professional-services firm is seeking a Senior Data Privacy Manager on a twelve-month contract to strengthen privacy governance and support client-facing compliance projects.',
    responsibilities: [
      'Review and improve privacy policies and procedures',
      'Lead data protection impact assessments',
      'Advise project teams on privacy-by-design',
      'Support incident response and regulatory correspondence',
    ],
    qualifications: [
      'Substantial data protection experience',
      'Recognised privacy certification preferred',
      'Experience in advisory or professional-services settings',
      'Clear, practical communication style',
    ],
    competencies: ['Technical expertise', 'Practical advice', 'Attention to detail', 'Client focus'],
  },
  {
    id: 'operations-director-infrastructure',
    ref: 'CTS-SAMPLE-1050',
    title: 'Operations Director',
    location: 'Birmingham, UK',
    arrangement: 'On-site',
    type: 'Permanent',
    seniority: 'Senior Management',
    industry: 'infrastructure',
    posted: '2026-10-04',
    summary: 'Lead safe, efficient operations across a transport infrastructure network.',
    overview:
      'An infrastructure operator is seeking an Operations Director to lead day-to-day operations, safety performance and continuous improvement across its network.',
    responsibilities: [
      'Lead operational delivery and safety performance',
      'Drive continuous improvement and efficiency',
      'Manage relationships with public-sector stakeholders',
      'Develop operational leaders and succession',
    ],
    qualifications: [
      'Senior operations leadership in infrastructure or transport',
      'Strong safety leadership record',
      'Experience in regulated environments',
      'Engineering or operations degree preferred',
    ],
    competencies: ['Operational excellence', 'Safety leadership', 'Stakeholder management', 'People development'],
  },
  {
    id: 'chief-operating-officer-retail',
    ref: 'CTS-SAMPLE-1051',
    title: 'Chief Operating Officer',
    location: 'Nairobi, Kenya',
    arrangement: 'Hybrid',
    type: 'Permanent',
    seniority: 'C-Suite',
    industry: 'consumer-retail',
    posted: '2026-10-02',
    summary: 'Run operations, supply chain and fulfilment for a consumer brand.',
    overview:
      'A consumer goods business is appointing a Chief Operating Officer to lead supply chain, fulfilment and operations as it expands across new markets.',
    responsibilities: [
      'Lead supply chain, logistics and fulfilment',
      'Scale operations across new markets',
      'Improve margin through operational efficiency',
      'Partner with commercial leaders on customer experience',
    ],
    qualifications: [
      'Senior operations leadership in consumer goods or retail',
      'Multi-market expansion experience',
      'Strong commercial and financial acumen',
      'Experience leading large, diverse teams',
    ],
    competencies: ['Operational strategy', 'Commercial focus', 'Scalability', 'Leadership presence'],
  },
  {
    id: 'senior-development-manager',
    ref: 'CTS-SAMPLE-1052',
    title: 'Senior Development Manager',
    location: 'London, UK',
    arrangement: 'Hybrid',
    type: 'Permanent',
    seniority: 'Specialist',
    industry: 'real-estate',
    salary: '£95,000–£115,000 + bonus (sample figure)',
    posted: '2026-09-30',
    summary: 'Lead mixed-use development projects from acquisition to completion.',
    overview:
      'A real estate developer is recruiting a Senior Development Manager to lead mixed-use schemes through planning, design and delivery.',
    responsibilities: [
      'Lead development projects from acquisition to completion',
      'Manage consultant teams, budgets and programmes',
      'Secure planning consents and manage stakeholders',
      'Embed sustainability into project delivery',
    ],
    qualifications: [
      'Development management experience on mixed-use schemes',
      'Strong planning and stakeholder experience',
      'Relevant degree or professional membership',
      'Commercial and financial appraisal skills',
    ],
    competencies: ['Project leadership', 'Negotiation', 'Commercial awareness', 'Sustainability focus'],
  },
  {
    id: 'head-of-government-affairs',
    ref: 'CTS-SAMPLE-1053',
    title: 'Head of Government Affairs',
    location: 'Brussels, Belgium',
    arrangement: 'Hybrid',
    type: 'Permanent',
    seniority: 'Senior Management',
    industry: 'government',
    posted: '2026-09-28',
    summary: 'Lead policy engagement for a regulated multinational.',
    overview:
      'A regulated multinational is seeking a Head of Government Affairs to lead policy engagement and represent the organisation with institutions and stakeholders.',
    responsibilities: [
      'Develop and deliver the government affairs strategy',
      'Represent the organisation with institutions and trade bodies',
      'Monitor and analyse policy developments',
      'Advise leadership on public policy risk and opportunity',
    ],
    qualifications: [
      'Senior public affairs or policy experience',
      'Strong network across relevant institutions',
      'Excellent written and verbal communication',
      'Additional languages an advantage',
    ],
    competencies: ['Political judgement', 'Discretion', 'Advocacy', 'Strategic communication'],
  },
];
